import * as ScrollAreaPrimitive from '@radix-ui/react-scroll-area';
import { type VariantProps, cva } from 'class-variance-authority';
import * as React from 'react';
import { cn } from '../lib/utils';

const scrollAreaVariants = cva('w-full rounded-[inherit]', {
  variants: {
    variant: {
      default: 'h-full',
      sidepanel: 'h-full [&>div]:!block [&>div]:!w-full',
      popover: 'max-h-[inherit] [&>div]:!block',
      light: 'h-full max-h-[inherit] overflow-x-auto',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

const scrollBarVariants = cva('flex touch-none select-none', {
  variants: {
    variant: {
      default: '',
      sidepanel: '',
      popover: '',
      light: '',
    },
    orientation: {
      vertical: '',
      horizontal: 'flex-col',
    },
  },
  compoundVariants: [
    {
      variant: ['default', 'sidepanel'],
      orientation: 'vertical',
      className: 'h-full w-3 py-3 px-1 bg-transparent z-sticky',
    },
    {
      variant: ['default', 'sidepanel'],
      orientation: 'horizontal',
      className: 'w-full h-3 px-3 py-1 bg-white border-t border-t-primary-5 z-sticky',
    },
    {
      variant: 'popover',
      orientation: 'vertical',
      className: 'w-2 px-0.5 !top-3 !bottom-2 right-0.5 bg-transparent',
    },
    {
      variant: 'popover',
      orientation: 'horizontal',
      className: 'w-full h-2 px-0 py-0.5 bg-transparent',
    },
    {
      variant: 'light',
      orientation: 'vertical',
      className: 'w-2 px-0.5 !top-3 !bottom-2 right-0.5 bg-transparent',
    },
    {
      variant: 'light',
      orientation: 'horizontal',
      className: 'w-full h-2 px-0 py-0.5 bg-transparent !bottom-0',
    },
  ],
  defaultVariants: {
    variant: 'default',
    orientation: 'vertical',
  },
});

const scrollThumbVariants = cva('relative flex-1 rounded-full', {
  variants: {
    variant: {
      default:
        'bg-primary-5 outline outline-1 outline-primary-10 hover:bg-primary-5 active:bg-primary-10',
      sidepanel:
        'bg-primary-5 outline outline-1 outline-primary-10 hover:bg-primary-5 active:bg-primary-10',
      popover: 'bg-primary-40 hover:bg-primary-50 active:bg-primary-60',
      light: 'bg-primary-3 hover:bg-primary-5 active:bg-primary-10',
    },
    orientation: {
      vertical: '',
      horizontal: '',
    },
  },
  compoundVariants: [
    { variant: ['default', 'sidepanel'], orientation: 'vertical', className: 'min-h-10' },
    { variant: ['default', 'sidepanel'], orientation: 'horizontal', className: 'min-w-10' },
    { variant: 'popover', orientation: 'vertical', className: 'min-h-[30px]' },
    { variant: 'popover', orientation: 'horizontal', className: 'min-w-[30px]' },
    { variant: 'light', orientation: 'vertical', className: 'min-h-[30px]' },
    { variant: 'light', orientation: 'horizontal', className: 'min-w-[30px]' },
  ],
  defaultVariants: {
    variant: 'default',
    orientation: 'vertical',
  },
});

type ScrollAreaVariant = VariantProps<typeof scrollAreaVariants>['variant'];

interface ScrollAreaProps
  extends React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Root>,
    VariantProps<typeof scrollAreaVariants> {
  viewportRef?: React.Ref<HTMLDivElement>;
  hideHorizontal?: boolean;
  hideVertical?: boolean;
}

const ScrollArea = React.forwardRef<
  React.ComponentRef<typeof ScrollAreaPrimitive.Root>,
  ScrollAreaProps
>(
  (
    {
      className,
      children,
      viewportRef,
      variant = 'default',
      hideHorizontal = false,
      hideVertical = false,
      ...props
    },
    ref,
  ) => (
    <ScrollAreaPrimitive.Root
      ref={ref}
      type={variant === 'popover' ? 'auto' : 'always'}
      className={cn('relative overflow-hidden w-full min-w-0', className)}
      {...props}
    >
      <ScrollAreaPrimitive.Viewport ref={viewportRef} className={scrollAreaVariants({ variant })}>
        {children}
      </ScrollAreaPrimitive.Viewport>
      {!hideVertical && (
        <ScrollBar
          orientation="vertical"
          variant={variant}
          forceMount={variant !== 'popover' && variant !== 'light' ? true : undefined}
        />
      )}
      {!hideHorizontal && (
        <ScrollBar
          orientation="horizontal"
          variant={variant}
          forceMount={variant !== 'popover' && variant !== 'light' ? true : undefined}
        />
      )}
      <ScrollAreaPrimitive.Corner
        className={cn(
          variant !== 'popover' && variant !== 'light'
            ? 'bg-white w-3 h-3'
            : 'bg-transparent w-2 h-2',
        )}
      />
    </ScrollAreaPrimitive.Root>
  ),
);
ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName;

interface ScrollBarProps
  extends React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar> {
  variant?: ScrollAreaVariant;
}

const ScrollBar = React.forwardRef<
  React.ComponentRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>,
  ScrollBarProps
>(({ className, orientation = 'vertical', variant = 'default', forceMount, ...props }, ref) => (
  <ScrollAreaPrimitive.ScrollAreaScrollbar
    ref={ref}
    orientation={orientation}
    forceMount={forceMount || undefined}
    className={cn(scrollBarVariants({ variant, orientation }), className)}
    {...props}
  >
    <ScrollAreaPrimitive.ScrollAreaThumb
      className={scrollThumbVariants({ variant, orientation })}
    />
  </ScrollAreaPrimitive.ScrollAreaScrollbar>
));
ScrollBar.displayName = ScrollAreaPrimitive.ScrollAreaScrollbar.displayName;

export { ScrollArea, ScrollBar };
export type { ScrollAreaVariant };
